import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { addressSearchAPI } from '../../api/address';
import { isCancelled } from '../../lib/client';

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

// 주소 검색 상태 — 입력 디바운스, 이전 요청 취소, 더 보기(페이지) 를 한 곳에서 다룬다.
// status: idle(2자 미만) | loading | ready | empty | error
export default function useAddressSearch() {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const source = useRef(null);   // 진행 중인 요청의 CancelToken
  const timer = useRef(null);    // 디바운스 타이머
  const lastQuery = useRef('');  // 가장 최근 확정 검색어 — 늦게 도착한 옛 응답을 버리는 기준

  const cancelPending = useCallback(() => {
    if (source.current) {
      source.current.cancel();
      source.current = null;
    }
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const run = useCallback(async (q, nextPage, append) => {
    cancelPending();
    const src = axios.CancelToken.source();
    source.current = src;
    setStatus('loading');
    setError('');

    const resp = await addressSearchAPI({ q, page: nextPage }, src.token);
    if (isCancelled(resp) || lastQuery.current !== q) return;
    source.current = null;

    if (!resp || resp.status !== 200) {
      setStatus('error');
      setError((resp && resp.data && resp.data.message) || '주소 검색에 실패했습니다.');
      return;
    }
    const data = resp.data || {};
    const fresh = Array.isArray(data.items) ? data.items : [];
    setItems((prev) => {
      if (!append) return fresh;
      const seen = new Set(prev.map((i) => i.id));
      return [...prev, ...fresh.filter((i) => !seen.has(i.id))];
    });
    setPage(nextPage);
    setHasMore(Boolean(data.hasMore));
    setStatus(append || fresh.length ? 'ready' : 'empty');
  }, [cancelPending]);

  // 입력값 변경 — 2자부터 300ms 뒤 자동 검색
  const search = useCallback((value) => {
    setQuery(value);
    const q = value.trim().replace(/\s+/g, ' ');
    lastQuery.current = q;
    cancelPending();
    if (q.length < MIN_QUERY) {
      setItems([]);
      setHasMore(false);
      setStatus('idle');
      return;
    }
    timer.current = setTimeout(() => run(q, 1, false), DEBOUNCE_MS);
  }, [cancelPending, run]);

  // Enter — 기다리지 않고 바로 검색
  const submit = useCallback(() => {
    const q = query.trim().replace(/\s+/g, ' ');
    if (q.length < MIN_QUERY) return;
    lastQuery.current = q;
    run(q, 1, false);
  }, [query, run]);

  const loadMore = useCallback(() => {
    if (status === 'loading' || !hasMore || !lastQuery.current) return;
    run(lastQuery.current, page + 1, true);
  }, [status, hasMore, page, run]);

  const reset = useCallback(() => {
    cancelPending();
    lastQuery.current = '';
    setQuery('');
    setItems([]);
    setPage(1);
    setHasMore(false);
    setStatus('idle');
    setError('');
  }, [cancelPending]);

  useEffect(() => cancelPending, [cancelPending]);

  return { query, items, status, error, hasMore, search, submit, loadMore, reset };
}
